import TrackingService from '@/services/tracking';
import { theme } from '@/styles';
import {
  BasicTooltipRef,
  Box,
  FilterChip,
  Icongraphy,
} from '@design-system/pc';
import React, { ReactNode, useEffect, useRef } from 'react';
import styled from 'styled-components';

type listType<T> = {
  code: string;
  name?: string;
  value?: string;
  text?: string;
} & T;

interface CategoryChipsProps<T, VALUE> extends React.CSSProperties {
  currentValue?: VALUE | VALUE[];
  onClick?: (e: VALUE) => void;
  onChange?: (e: VALUE) => void;
  list?: listType<T>[];
  children?: ReactNode;
  disabled?: boolean;
  hasRefresh?: boolean;
  handleReset?: () => void;
  type?: 'multi' | 'single';
  useCheckIcon?: boolean;
}

//* 사용법 *//
// 개별 사용시 props에 text를 입력
// 여러가지의 리스트를 보낼시 list에 string 배열을 보내주면됨

const CategoryChips = <T, VALUE>(props: CategoryChipsProps<T, VALUE>) => {
  const ref = useRef<BasicTooltipRef>(null);

  useEffect(() => {
    ref.current && ref.current?.open(5);
  }, []);

  return (
    <>
      {props.type === 'multi' ? (
        <ChipBox {...{ props }} {...TrackingService.tagGTM('shop_search')}>
          {props.hasRefresh && (
            <RefreshIconBox
              onClick={props.handleReset && props.handleReset}
              {...TrackingService.tagGTM('shop_search_shop_type_reset_button')}
            >
              <Icongraphy variant="ic_refresh" size={20} />
            </RefreshIconBox>
          )}
          {props.list ? (
            props.list?.map((item: listType<T>) => {
              const isCurrent = Array.isArray(props.currentValue)
                ? props?.currentValue?.includes(item as any)
                : item.code === (props?.currentValue as any).code;
              return (
                <FilterChip
                  isAlarm={false}
                  icon={
                    props.useCheckIcon
                      ? {
                          variant: 'ic_check_bold',
                          align: 'left',
                        }
                      : undefined
                  }
                  isChecked={isCurrent}
                  key={item.code}
                  onClick={() => props.onChange && props.onChange(item as any)}
                  {...TrackingService.tagGTM(
                    'shop_search_shop_type_multi_button',
                    {
                      'option-value': item.code,
                    },
                  )}
                >
                  {item.name || item.text}
                </FilterChip>
              );
            })
          ) : (
            <FilterChip
              isChecked={props?.currentValue === props.children}
              onClick={() =>
                props.onClick && props.onClick(props.children as any)
              }
              {...(TrackingService.tagGTM(
                'shop_search_shop_type_single_button',
              ),
              {
                'option-value': props?.currentValue,
              })}
            >
              {props.children}
            </FilterChip>
          )}
        </ChipBox>
      ) : (
        <FilterChip
          isChecked={props?.currentValue === props.children}
          disabled={props.disabled || false}
          onClick={() => props.onClick && props.onClick(props.children as any)}
          {...(TrackingService.tagGTM('shop_search_shop_type_single_button'),
          {
            'option-value': props?.currentValue,
          })}
        >
          <Icongraphy
            variant="ic_check"
            fillColor={
              props.disabled
                ? theme.colors.neutral_colors.gray[100]
                : props?.currentValue
                ? theme.colors.accent_colors.primary.default
                : theme.colors.neutral_colors.gray[500]
            }
          />
          {props.children}
        </FilterChip>
      )}
    </>
  );
};

export default CategoryChips;

const ChipBox = styled(Box)`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ props }) => props.gap || '8px 12px'};
`;

const RefreshIconBox = styled(Box)`
  cursor: pointer;
  margin-right: 6px;
`;
